import { ICampaignInputOptions } from '../../models/campaign-input-options.model';
import { AdvertiserModel } from '../../models/advertiser.model';
import { makeAutoObservable, runInAction } from 'mobx';
import { CampaignInputOptionKey, CampaignsInputOptionsApi } from '../../apis/campaign-input-options.api';
import { StrategyModel } from '../../models/strategy.model';
import { PretargetingModel } from '../../models/pretargeting.model';
import { capitalCase } from 'change-case';
import { BannerSize, IEntity } from '../../models/campaign.model';
import { ISegmentation } from '../../models/campaign-segmentation.model';
import { AccountsModel } from '../../models/account.model';

export class CampaignInputOptionsStore {
	private advertisers: AdvertiserModel[] = [];
	private strategies: StrategyModel[] = [];
	private pretargetings: PretargetingModel[] = [];
	private accounts: AccountsModel[] = [];
	private key_generators: string[] = [];
	private device_types: string[] = [];
	private active_browsers: string[] = [];
	private operating_systems: string[] = [];
	private banner_sizes: BannerSize[] = [];
	private phases: IEntity[] = [];
	private isLoading = false;
	private error: Error | null = null;
	private webapps_cid_segments: ISegmentation[] = [];
	design_score_pickers: string[] = [];
	format_score_pickers: string[] = [];

	constructor() {
		makeAutoObservable(this);
	}

	fetch(keys: CampaignInputOptionKey[]): Promise<void | ICampaignInputOptions> {
		this.setIsLoading(true);
		return CampaignsInputOptionsApi.get(keys)
			.then(res => {
				runInAction(() => {
					this.setInputOptions(res as ICampaignInputOptions);
				});
			})
			.catch((error: Error) => {
				runInAction(() => {
					this.error = error;
				});
			})
			.finally(() => {
				runInAction(() => {
					this.setIsLoading(false);
				});
			});
	}

	setInputOptions(res: ICampaignInputOptions): void {
		if (res.advertisers) {
			this.advertisers = res.advertisers.map(advertiser => new AdvertiserModel(advertiser));
		}
		if (res.strategies) {
			this.strategies = res.strategies.map(strategy => new StrategyModel(strategy));
		}
		if (res.pretargetings) {
			this.pretargetings = res.pretargetings.map(pretargeting => new PretargetingModel(pretargeting));
		}
		if (res.key_generators) {
			this.key_generators = res.key_generators;
		}
		if (res.device_types) {
			this.device_types = res.device_types.map(deviceType => capitalCase(deviceType));
		}
		if (res.active_browsers) {
			this.active_browsers = res.active_browsers.map(activeBrowser => capitalCase(activeBrowser));
		}
		if (res.operating_systems) {
			this.operating_systems = res.operating_systems.map(operatingSystem => capitalCase(operatingSystem));
		}
		if (res.banner_sizes) {
			this.banner_sizes = res.banner_sizes;
		}
		if (res.webapps_cid_segments) {
			this.webapps_cid_segments = res.webapps_cid_segments;
		}
		if (res.accounts) {
			this.accounts = res.accounts.map(account => new AccountsModel(account));
		}
		if (res.design_score_pickers) {
			this.design_score_pickers = res.design_score_pickers;
		}
		if (res.format_score_pickers) {
			this.format_score_pickers = res.format_score_pickers;
		}
		if (res.phases) {
			this.phases = res.phases;
		}
	}

	getKeyGenerators(): string[] {
		return this.key_generators;
	}

	getIsLoading(): boolean {
		return this.isLoading;
	}

	setIsLoading(isLoading: boolean) {
		this.isLoading = isLoading;
	}

	getError(): Error | null {
		return this.error;
	}

	getAdvertisers(): AdvertiserModel[] {
		return this.advertisers;
	}

	getStrategies(): StrategyModel[] {
		return this.strategies;
	}

	getPretargetings(): PretargetingModel[] {
		return this.pretargetings;
	}

	getDeviceTypes(): string[] {
		return this.device_types;
	}

	getBrowsers(): string[] {
		return this.active_browsers;
	}

	getOperatingSystems(): string[] {
		return this.operating_systems;
	}
	getBannerSizes(): BannerSize[] {
		return this.banner_sizes;
	}
	getWebappsCidSegments(): ISegmentation[] {
		return this.webapps_cid_segments;
	}

	getAccounts(): AccountsModel[] {
		return this.accounts;
	}

	getPhases(): IEntity[] {
		return this.phases;
	}
}
